import { useCallback, useRef } from 'react';
import { useVault } from '../session/useVault';
import { failedBeforeQueued, pendingText } from '../vault/writeQueue';
import { pinAtFront, pinnedNooks, withoutPinned } from './pinned';
import { keeping, parseWorkspace } from './workspace';
import { asTheyStand } from './loadWorkspaces';
import { withVisibility } from '../plan/edits';
import { DESK_PATH } from '../desk/deskContainer';
import { readDeskRaw } from '../desk/useDeskContainer';
import { asWrites, unpinNook } from '../desk/deskActs';
import type { NooksNow } from '../desk/deskActs';
import type { Workspace } from './workspace';
import type { FileWrite } from '../vault/sparseWriter';

/** What the sync state says when an unpin, a pin, an archive or a restore could not read what it
 *  is made on. */
export const NOT_UNPINNED = 'Not unpinned: the vault could not be read. Try again.';
export const NOT_PINNED = 'Not pinned: the vault could not be read. Try again.';
export const NOT_ARCHIVED = 'Not archived: the vault could not be read. Try again.';
export const NOT_RESTORED = 'Not restored: the vault could not be read. Try again.';

/**
 * The writes an unpin makes: the nook's own `pinned:` line dropped and, where the desk container
 * exists, the cards the desk shows but no list holds listed, as every desk act lists them: the nook
 * among them where the pin was all that held it there, as the app's unpin keeps it. One archived
 * or held inside another nook stood nowhere on the desk, and stays off it.
 */
export function unpinWrites(
  path: string,
  nookRaw: string,
  deskRaw: string | null,
  nooks: NooksNow,
): FileWrite[] {
  const text = withoutPinned(nookRaw);
  if (text === nookRaw) return [];
  const nook = parseWorkspace(path, nookRaw);
  if (!nook) return [{ path, text, change: withoutPinned }];
  return asWrites(unpinNook(nook, nooks), (p) => (p === path ? nookRaw : p === DESK_PATH ? deskRaw : null));
}

/**
 * Pin and unpin, as one write to the nook's own file: the `pinned:` line made
 * again on the file as it stands when the change goes out, every other key
 * left alone — but a draft's mark, since pinning one keeps it. A discrete
 * press, so it is sent rather than left to the debounce. Where the nook cannot
 * be read, nothing is queued, and the sync state says so.
 */
export function usePinning(workspaces: readonly Workspace[]) {
  const { state, commit } = useVault();
  const seen = useRef(workspaces);
  seen.current = workspaces;

  const edit = useCallback(
    async (path: string, change: (raw: string) => string, failed: string) => {
      if (state.status !== 'ready') return;
      let raw: string | null;
      try {
        raw = pendingText(path) ?? (await state.vault.readFile(path));
      } catch {
        failedBeforeQueued(failed);
        return;
      }
      if (raw === null) return;
      const text = change(raw);
      if (text !== raw) await commit([{ path, text, change }]);
    },
    [state, commit],
  );

  const pin = useCallback(
    (nook: Pick<Workspace, 'path'>) => {
      const pins = pinnedNooks(workspaces).map((w) => w.pinned);
      return edit(nook.path, keeping((raw) => pinAtFront(raw, pins)), NOT_PINNED);
    },
    [workspaces, edit],
  );

  /** The nook and the container are read first, and their writes queued together or not at all:
   *  where either cannot be read, nothing is, and the sync state says so. */
  const unpin = useCallback(
    async (nook: Pick<Workspace, 'path'>) => {
      if (state.status !== 'ready') return;
      let raw: string | null;
      let deskRaw: string | null;
      try {
        raw = pendingText(nook.path) ?? (await state.vault.readFile(nook.path));
        deskRaw = await readDeskRaw(state.vault);
      } catch {
        failedBeforeQueued(NOT_UNPINNED);
        return;
      }
      if (raw === null) return;
      const writes = unpinWrites(nook.path, raw, deskRaw, () => asTheyStand(seen.current));
      if (writes.length > 0) await commit(writes);
    },
    [state, commit],
  );

  /** Files a nook away, everything on it as it was, or brings it back. A draft
   *  filed away keeps its mark: it is let go, and no list shows it again. */
  const archive = useCallback(
    (nook: Pick<Workspace, 'path'>, archived: boolean) =>
      edit(nook.path, (raw) => withVisibility(raw, archived ? 'archived' : 'visible'), archived ? NOT_ARCHIVED : NOT_RESTORED),
    [edit],
  );

  return { pin, unpin, archive };
}
